import { generateGuid } from 'shared/src/utils/shared.js';

import { ConditionalLogicTypes, PagePlaceholderProperties } from '@/util/resources';

import { FormPageInfo, IConditionPage, LogicAction } from '@/components/Conditions/models/model';

type ConditionNode = {
	type?: string;
	field?: { page?: number };
	settings?: ConditionNode[];
};

type ContentRow = {
	pageIndex?: number;
	pageId?: string;
	type?: string;
	[key: string]: unknown;
};

/**
 * Ensures every row has a stable pageId shared with other rows on the same pageIndex.
 * Migrates existing forms that only have pageIndex. Idempotent.
 */
export function ensureRowsHavePageIds<T extends ContentRow>(rows: T[] | undefined | null): T[] {
	if (!rows?.length) return rows || [];

	const pageIdByIndex: Record<number, string> = {};
	const claimedPageIds = new Set<string>();

	rows.forEach((row) => {
		const pageIndex = typeof row.pageIndex === 'number' ? row.pageIndex : 0;
		const existingId = row.pageId;

		if (!existingId || pageIdByIndex[pageIndex]) return;

		if (claimedPageIds.has(existingId)) return;

		pageIdByIndex[pageIndex] = existingId;
		claimedPageIds.add(existingId);
	});

	Object.keys(
		rows.reduce<Record<number, true>>((acc, row) => {
			const pageIndex = typeof row.pageIndex === 'number' ? row.pageIndex : 0;
			acc[pageIndex] = true;
			return acc;
		}, {}),
	).forEach((key) => {
		const pageIndex = Number(key);
		if (!pageIdByIndex[pageIndex]) {
			const pageId = generateGuid();
			pageIdByIndex[pageIndex] = pageId;
			claimedPageIds.add(pageId);
		}
	});

	return rows.map((row) => {
		const pageIndex = typeof row.pageIndex === 'number' ? row.pageIndex : 0;
		const pageId = pageIdByIndex[pageIndex];

		if (row.pageId === pageId && row.pageIndex === pageIndex) return row;

		return { ...row, pageIndex, pageId };
	});
}

/** Unique pages in display order. Includes placeholder-only pages. */
export function getFormPages(rows: ContentRow[] | undefined | null): FormPageInfo[] {
	const ensured = ensureRowsHavePageIds(rows);
	const pagesByIndex = new Map<number, FormPageInfo>();

	ensured.forEach((row) => {
		const pageIndex = typeof row.pageIndex === 'number' ? row.pageIndex : 0;
		if (pagesByIndex.has(pageIndex)) return;

		pagesByIndex.set(pageIndex, {
			id: row.pageId as string,
			pageIndex,
		});
	});

	return Array.from(pagesByIndex.values()).sort((a, b) => a.pageIndex - b.pageIndex);
}

export function getPageIdForPageIndex(rows: ContentRow[] | undefined | null, pageIndex: number): string | undefined {
	const page = getFormPages(rows).find((entry) => entry.pageIndex === pageIndex);
	return page?.id;
}

export function resolvePageById(pages: FormPageInfo[], pageId: string | undefined | null): FormPageInfo | undefined {
	if (!pageId) return undefined;
	return pages.find((page) => page.id === pageId);
}

/**
 * Highest pageIndex among fields referenced by the rule's conditions.
 * Form language / non-field conditions have no page and do not affect the result.
 * Returns null when no field conditions exist (all pages are valid targets).
 */
export function getLatestConditionPage(settings: ConditionNode[] | undefined | null): number | null {
	let latest: number | null = null;

	const walk = (nodes: ConditionNode[] | undefined | null) => {
		(nodes || []).forEach((node) => {
			if (node?.type === ConditionalLogicTypes.group) {
				walk(node.settings);
				return;
			}

			if (node?.type !== ConditionalLogicTypes.condition) return;

			const page = node.field?.page;
			if (typeof page !== 'number') return;

			if (latest === null || page > latest) {
				latest = page;
			}
		});
	};

	walk(settings);
	return latest;
}

/** Pages after the latest condition page. If latest is null, all pages are selectable. */
export function getSelectablePageTargets(pages: FormPageInfo[], latestConditionPage: number | null): FormPageInfo[] {
	if (latestConditionPage === null || typeof latestConditionPage === 'undefined') {
		return pages;
	}

	return pages.filter((page) => page.pageIndex > latestConditionPage);
}

export function toConditionPage(page: FormPageInfo): IConditionPage {
	return {
		id: page.id,
		pageIndex: page.pageIndex,
	};
}

/** Refresh stored page.pageIndex from the live page catalog; clear if the pageId is gone. */
export function syncLogicActionPageTarget(action: LogicAction, pages: FormPageInfo[]): LogicAction {
	if (!action?.page?.id) return action;

	const livePage = resolvePageById(pages, action.page.id);
	if (!livePage) {
		return { ...action, page: undefined };
	}

	if (action.page.pageIndex === livePage.pageIndex) return action;

	return {
		...action,
		page: toConditionPage(livePage),
	};
}

export function getPageDisplayLabel(pageIndex: number): string {
	return `Page ${pageIndex + 1}`;
}

export function isPagePlaceholderRow(row: ContentRow | undefined | null): boolean {
	return row?.type === PagePlaceholderProperties.type;
}

/** True when any page action targets the given pageId. */
export function logicsReferencePageId(logics: Array<{ actions?: LogicAction[] }> | undefined | null, pageId: string | undefined | null): boolean {
	if (!pageId || !logics?.length) return false;

	return logics.some((logic) => (logic.actions || []).some((action) => action.page?.id === pageId));
}

/**
 * Clears page targets that point at a deleted page.
 * Leaves the action in place (marked incomplete by validation).
 */
export function clearPageActionsForPageId<T extends { actions?: LogicAction[] }>(logics: T[] | undefined | null, pageId: string | undefined | null): T[] {
	if (!pageId || !logics?.length) return logics || [];

	return logics.map((logic) => ({
		...logic,
		actions: (logic.actions || []).map((action) => {
			if (action.page?.id !== pageId) return action;
			return { ...action, page: undefined };
		}),
	}));
}

/**
 * Syncs every page action's displayed pageIndex from the live page catalog.
 * Clears targets whose pageId no longer exists.
 */
export function syncLogicActionsPageTargets<T extends { actions?: LogicAction[] }>(
	logics: T[] | undefined | null,
	rows: ContentRow[] | undefined | null,
): T[] {
	if (!logics?.length) return logics || [];

	const pages = getFormPages(rows);

	return logics.map((logic) => ({
		...logic,
		actions: (logic.actions || []).map((action) => syncLogicActionPageTarget(action, pages)),
	}));
}

/**
 * After conditions or page order change, drop page targets that are no longer valid
 * for that rule (must be after the latest condition page).
 */
export function clearInvalidPageActionTargets<T extends { settings?: ConditionNode[]; actions?: LogicAction[] }>(
	logics: T[] | undefined | null,
	rows: ContentRow[] | undefined | null,
): T[] {
	if (!logics?.length) return logics || [];

	const pages = getFormPages(rows);

	return logics.map((logic) => {
		const latestConditionPage = getLatestConditionPage(logic.settings);
		const selectablePages = getSelectablePageTargets(pages, latestConditionPage);
		const selectableIds = new Set(selectablePages.map((page) => page.id));

		return {
			...logic,
			actions: (logic.actions || []).map((action) => {
				const synced = syncLogicActionPageTarget(action, pages);
				if (!synced.page?.id) return synced;
				if (selectableIds.has(synced.page.id)) return synced;
				return { ...synced, page: undefined };
			}),
		};
	});
}
